import { useCallback, useMemo, useState } from "react";
import { Alert, Box, Button, Theme, Typography } from "@mui/material";
import moment from "moment";
import { IVacationBidPackage, VacationAwardingProgressStatus, VacationBidRound } from "@vmosolutions/oc-interfaces";

import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";

import { VACATION_BID_TEST_ID } from "../testIds";
import { displaySlotId } from "../helpers";
import {
  awardingSummary,
  crewName,
  crewNamesBySlot,
  entitlementReached,
  leftoverPlacement,
  nextCrewToAward,
  pendingSlotIds,
  round2OpenedBeforePublishing,
  roundCapReached,
} from "./awardingHelpers";
import { IVacationAwarding } from "./useVacationAwarding";
import { AwardingConfirmDialog, IAwardingConfirm } from "./AwardingConfirmDialog";
import { AwardingSummary } from "./AwardingSummary";
import { BlockCapacityGrid } from "./BlockCapacityGrid";
import { AwardQueue } from "./AwardQueue";
import { CrewAwardPanel } from "./CrewAwardPanel";
import { LeftoverPlacement } from "./CrewLeftoverDays";

export interface IAwardBidsTabProps {
  awarding: IVacationAwarding;
  /** The saved package, for its round 1 cap and second window. */
  pkg: IVacationBidPackage;
  round: VacationBidRound;
  /** Every round that can be awarded now, earliest first. Only one on a single-round package. */
  rounds: VacationBidRound[];
  showSlotIdYear: boolean;
  onRoundChange: (round: VacationBidRound) => void;
}

const AwardBidsTabStyles = (theme: Theme): any => ({
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  width: "100%",
  height: "100%",
  boxSizing: "border-box",
  padding: "10px",
  overflowY: "auto",
  position: "relative",
  "& .award_spinner": { position: "absolute", top: 0, right: 0, bottom: 0, left: 0, zIndex: 10 },
  "& .award_toolbar": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& .award_round": { fontSize: 12, color: theme.custom.forms.labels },
    "& .award_round_spacer": { flex: 1 },
    "& .MuiButtonBase-root": { padding: "2px 8px", fontSize: 12 },
  },
  "& .MuiAlert-root": { padding: "0 12px", fontSize: 13 },
  "& .award_workspace": {
    flex: 1,
    minHeight: "340px",
    display: "grid",
    gridTemplateColumns: "240px minmax(0, 1fr)",
    gap: "10px",
  },
  "& .award_empty": { color: theme.custom.forms.labels, textAlign: "center", marginTop: "40px" },
});

/**
 * Award Bids tab - the scheduler works through the crew in seniority order, awarding each from
 * their ranked bids or assigning blocks they did not rank. Every action is saved as it happens.
 */
export const AwardBidsTab = (props: IAwardBidsTabProps): JSX.Element => {
  const { awarding, pkg, round, rounds, showSlotIdYear, onRoundChange } = props;
  const { view, loading, saving, pending, load, save, dismissWarning } = awarding;

  const [chosenUserId, setChosenUserId] = useState<string | null>(null);
  const [showCurrentOnly, setShowCurrentOnly] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<IAwardingConfirm | null>(null);

  const crew = useMemo(() => view?.crew ?? [], [view]);

  // The chosen crew member while they are still in the package; otherwise whoever the scheduler
  // would reach next.
  const selectedUserId = useMemo(
    () =>
      chosenUserId && crew.some((z) => z.userId === chosenUserId)
        ? chosenUserId
        : (nextCrewToAward(crew, null) ?? crew[0]?.userId ?? null),
    [chosenUserId, crew]
  );
  const selected = useMemo(() => crew.find((z) => z.userId === selectedUserId) ?? null, [crew, selectedUserId]);
  const slotsById = useMemo(() => new Map((view?.slots ?? []).map((z) => [z.slotId, z])), [view]);
  const usageBySlot = useMemo(() => new Map((view?.usage ?? []).map((z) => [z.slotId, z])), [view]);
  const namesBySlot = useMemo(() => crewNamesBySlot(crew), [crew]);
  const summary = useMemo(() => awardingSummary(view?.usage ?? [], crew), [view, crew]);

  // What is still being saved for the selected crew member, so each row can show its own state.
  const selectedPending = useMemo(() => {
    return {
      adds: pendingSlotIds(pending, selectedUserId, "add"),
      removes: pendingSlotIds(pending, selectedUserId, "remove"),
      progress: pending.some((z) => z.userId === selectedUserId && z.kind === "progress"),
      leftover: pending.some((z) => z.userId === selectedUserId && z.kind === "leftover"),
      shortBlock: pending.some((z) => z.userId === selectedUserId && z.kind === "shortBlock"),
    };
  }, [pending, selectedUserId]);

  const writesAllowed = !!view?.writesAllowed;
  const capReached = !!selected && roundCapReached(pkg.maxAwardsFirstRound, selected, round, selectedPending.adds.size);
  const fullEntitlement = !!selected && entitlementReached(selected, slotsById, selectedPending.adds);

  const award = useCallback(
    (slotId: string) => {
      if (!selected || selectedPending.adds.has(slotId) || selected.awards.some((z) => z.slotId === slotId)) return;
      const label = displaySlotId(slotId, showSlotIdYear);
      const name = crewName(selected);
      const usage = usageBySlot.get(slotId);
      const ranked = selected.bid?.preferences.some((z) => z.slotId === slotId) ?? false;
      const full = !!usage && usage.remaining <= 0;

      const send = (): void => {
        void save({ add: [{ userId: selected.userId, slotId }] });
      };
      if (ranked && !full) {
        send();
        return;
      }

      const lines: string[] = [];
      if (!ranked) lines.push(`${name} did not rank ${label}, so it will be recorded as assigned.`);
      if (full && usage)
        lines.push(`${label} is full: ${usage.awarded} of ${usage.capacity} places are already awarded.`);
      setConfirm({
        title: full ? "Award a full block?" : "Assign a block?",
        lines,
        confirmLabel: ranked ? "Award" : "Assign",
        onConfirm: send,
      });
    },
    [selected, selectedPending, showSlotIdYear, usageBySlot, save]
  );

  const undo = useCallback(
    (slotId: string) => {
      if (!selected) return;
      const send = (): void => {
        void save({ remove: [{ userId: selected.userId, slotId }] });
      };

      // Taking the block back deletes the award, and the leftover days sitting on it go with it.
      const placement = leftoverPlacement(selected);
      if (placement?.slotId !== slotId) {
        send();
        return;
      }
      setConfirm({
        title: "Take back the block holding their leftover days?",
        lines: [
          `${crewName(selected)}'s ${placement.days} leftover day${placement.days === 1 ? "" : "s"} sit ${placement.side} ${displaySlotId(slotId, showSlotIdYear)}.`,
          "Taking the block back clears that placement, and the days will need putting on another block they hold.",
        ],
        confirmLabel: "Take back",
        onConfirm: send,
      });
    },
    [selected, save, showSlotIdYear]
  );

  const setProgress = useCallback(
    (status: VacationAwardingProgressStatus | null): void => {
      if (!selected) return;
      const userId = selected.userId;
      void save({ progress: [{ userId, status }] });
      if (status === null) return;
      // Moves on down the queue straight away, reckoned with this crew member's new progress in
      // place. A save that fails leaves them showing as they were.
      const updated = crew.map((z) => (z.userId === userId ? { ...z, progress: status } : z));
      setChosenUserId(nextCrewToAward(updated, userId) ?? userId);
    },
    [selected, save, crew]
  );

  const placeLeftover = useCallback(
    (placement: LeftoverPlacement): void => {
      if (selected) void save({ leftover: [{ userId: selected.userId, placement }] });
    },
    [selected, save]
  );

  // Saved at once: moving it back is as easy as moving it.
  const makeShort = useCallback(
    (slotId: string): void => {
      if (selected) void save({ shortBlock: [{ userId: selected.userId, slotId }] });
    },
    [selected, save]
  );

  const dismissSelectedWarning = useCallback(
    (warningId: string): Promise<boolean> =>
      selected ? dismissWarning(selected.userId, warningId) : Promise.resolve(false),
    [selected, dismissWarning]
  );

  const currentBidSlotIds = useMemo(
    () => (showCurrentOnly && selected?.bid ? new Set(selected.bid.preferences.map((z) => z.slotId)) : null),
    [showCurrentOnly, selected]
  );

  const readOnlyNotice = useMemo((): string | null => {
    if (!view || view.writesAllowed) return null;
    if (view.publication) {
      return `Round ${view.round} was published on ${moment(view.publication.publishedAt).format("MMM DD, YYYY HH:mm")}. Its awards are read-only.`;
    }
    return view.writesRefusedReason ?? "This round's awards cannot be changed right now.";
  }, [view]);

  // Round 2 is held shut until round 1 is published, so an unpublished round 1 past the second
  // window's open is not a cosmetic problem: crew cannot bid at all until someone presses publish.
  const round2Warning =
    !!view && round2OpenedBeforePublishing(pkg, view)
      ? `Round 2 bidding opened on ${moment(pkg.secondBidWindowOpen).format("MMM DD, YYYY")}, but round 1's awards ` +
        `have not been published - so crew cannot bid in round 2 yet. Publish round 1 to open it.`
      : null;

  return (
    <Box className="award_bids_tab" sx={AwardBidsTabStyles}>
      <SpinnerProgress className="award_spinner" loading={loading && !view} message="loading bids" />

      <div className="award_toolbar">
        {rounds.length > 1 ? (
          <>
            <Typography className="award_round">Awarding</Typography>
            {rounds.map((option) => (
              <Button
                key={option}
                size="small"
                variant={option === round ? "contained" : "outlined"}
                color={option === round ? "secondary" : "primary"}
                // Held while a round is loading, so the view cannot be left showing a round other
                // than the one lit up here.
                disabled={loading || saving}
                onClick={() => onRoundChange(option)}
                data-testid={VACATION_BID_TEST_ID.awardingRoundButton}
              >
                {`Round ${option}`}
              </Button>
            ))}
          </>
        ) : (
          <Typography className="award_round">{`Round ${round} awarding`}</Typography>
        )}
        <span className="award_round_spacer" />
        <Button size="small" variant="outlined" disabled={loading || saving} onClick={() => void load()}>
          Refresh
        </Button>
      </div>

      {!view && !loading && (
        <Typography variant="body2" className="award_empty">
          The package&apos;s bids could not be loaded. Refresh to try again.
        </Typography>
      )}

      {view && (
        <>
          {readOnlyNotice && <Alert severity={view.publication ? "info" : "warning"}>{readOnlyNotice}</Alert>}
          {round2Warning && <Alert severity="warning">{round2Warning}</Alert>}

          <AwardingSummary summary={summary} />

          <BlockCapacityGrid
            slots={view.slots}
            usageBySlot={usageBySlot}
            crewNamesBySlot={namesBySlot}
            showSlotIdYear={showSlotIdYear}
            onlySlotIds={currentBidSlotIds}
            onlyToggleLabel={showCurrentOnly ? "Show all blocks" : "Show current bids only"}
            onlyToggleDisabled={!selected?.bid}
            onToggleOnly={() => setShowCurrentOnly((current) => !current)}
            onSlotClick={writesAllowed && selected && !capReached && !fullEntitlement ? award : undefined}
          />

          {!crew.length && (
            <Typography variant="body2" className="award_empty">
              There are no crew in this package yet.
            </Typography>
          )}

          {!!crew.length && (
            <div className="award_workspace">
              <AwardQueue crew={crew} selectedUserId={selectedUserId} onSelect={setChosenUserId} />
              {selected && (
                <CrewAwardPanel
                  crew={selected}
                  round={round}
                  slotsById={slotsById}
                  usageBySlot={usageBySlot}
                  showSlotIdYear={showSlotIdYear}
                  maxAwardsFirstRound={pkg.maxAwardsFirstRound}
                  writesAllowed={writesAllowed}
                  capReached={capReached}
                  entitlementReached={fullEntitlement}
                  pendingAdds={selectedPending.adds}
                  pendingRemoves={selectedPending.removes}
                  progressPending={selectedPending.progress}
                  leftoverPending={selectedPending.leftover}
                  shortBlockPending={selectedPending.shortBlock}
                  onAward={award}
                  onUndo={undo}
                  onProgress={setProgress}
                  onPlaceLeftover={placeLeftover}
                  onMakeShort={makeShort}
                  onDismissWarning={dismissSelectedWarning}
                />
              )}
            </div>
          )}
        </>
      )}

      <AwardingConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </Box>
  );
};

export default AwardBidsTab;
